import Vue from 'vue'
import _ from 'lodash'
import { DEFAULT_PAGE_SIZE } from '@/config/app'
import { discard } from '@/services/functions/preserve&discard'

/** @import { GridColumnProps } from '@progress/kendo-vue-grid' */

/**
 * @typedef {Object} MasterFilter
 * @property {string} label
 * @property {NonNullable<*>} value
 * @property {string} [type]
 */

/**
 * @typedef {{extraparams:Object}&GridColumnProps} KendoColumn
 * @property {Object} extraparams
 */

/**
 * @typedef {Object} TableData
 * @property {{[key:string]:MasterFilter}} masterFilters
 * @property {Array<KendoColumn>} columns
 * @property {boolean} notUseLikeTags
 * @property {boolean} navigate
 * @property {boolean} selectable
 * @property {boolean} editable
 * @property {boolean} expandable
 * @property {boolean} keepSelection
 * @property {Object} customState
 * @property {{[key:string]:Function}} customGetters
 * @property {{[key:string]:Function}} customMutations
 * @property {{[key:string]:Function}} customActions
 * @property {boolean} namespaced
 * @property {() => Array<KendoColumn>} getTranslateColumns
 * @property {() => {[key:string]:MasterFilter}} getTranslateMasterFilters
 * @property {Array} rest
 */

/**
 * 
 * @param {Object} param0 
 * @param {{[key:string]:MasterFilter}} param0.masterFilters
 * @param {Array<KendoColumn>} param0.columns
 * @param {boolean} param0.notUseLikeTags
 * @param {boolean} param0.navigate
 * @param {boolean} param0.selectable
 * @param {boolean} param0.editable
 * @param {boolean} param0.expandable
 * @param {boolean} param0.keepSelection
 * @param {boolean} param0.keepEditions
 * @param {Object} param0.customState
 * @param {{[key:string]:Function}} param0.customGetters
 * @param {{[key:string]:Function}} param0.customMutations
 * @param {{[key:string]:Function}} param0.customActions
 * @param {boolean} param0.namespaced
 * @param {() => Array<KendoColumn>} param0.getTranslateColumns
 * @param {() => {[key:string]:MasterFilter}} param0.getTranslateMasterFilters
 * @param {Array} param0.rest
 * @returns 
 */
function _interface ({ masterFilters = {}, columns = [], notUseLikeTags = [], navigate = false, selectable = false, editable = false, expandable = false, keepSelection = true, keepEditions = false, customState = {}, customGetters = {}, customMutations = {}, customActions = {}, namespaced = true, groupable = true, getTranslateColumns: getColumns = null, getTranslateMasterFilters = null, ...rest }) {
  if (typeof getTranslateMasterFilters === 'function') {
    _.merge(masterFilters, getTranslateMasterFilters())
  }
  const getDefaultState = () => {
    return _.merge({
      paginationInfo: {
        total: 0,
        currentPage: 1,
        totalPages: 0,
        pageSize: DEFAULT_PAGE_SIZE
      },
      currentPage: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: null,
      order: null,
      filters: [],
      masterFilters: _.cloneDeep(masterFilters),
      notUseLikeTags: ['results', 'page', 'sort', 'order', ...notUseLikeTags],
      kendo: {
        columns,
        skip: 0,
        take: 10,
        sortable: true,
        sort: [],
        group: [],
        filterable: false,
        groupable: groupable,
        reorderable: true,
        filter: null,
        columnMenu: true,
        dataResult: null,
        selectable,
        expandable,
        editable,
        navigate,
        keepSelection: selectable && keepSelection,
        keepEditions: editable && keepEditions
      },
      clientFilterActive: true,
      langCode: '',
    }, customState)
  }
  
  const state = getDefaultState()
  
  const getters = _.merge({
    getPayloadCurrentPage: state => state.paginationInfo.currentPage,
    getPayloadPageSize: state => state.paginationInfo.pageSize,
    getPayloadTotal: state => state.paginationInfo.total,
    getPayLoadTotalPages: state => state.paginationInfo.totalPages,
    getMasterFilters: (state, getters, rootState, rootGetters) => {
      if (typeof getTranslateMasterFilters === 'function') {
        const langCode = rootGetters['auth/locale']
        if (!state.langCode) {
          state.langCode = langCode
          state.masterFilters = getTranslateMasterFilters()
          return state.masterFilters
        }
        if (state.langCode === langCode) {
          return state.masterFilters
        }
        state.langCode = langCode
        const _temp = Object.entries(getTranslateMasterFilters())
        _.each(_temp, ([key, item]) => {
          if (typeof item === 'object') {
            if (item.value === undefined || item.value === null) {
              item.value = ''
            }
            if (Object.hasOwnProperty.call(state.masterFilters, key)) {
              state.masterFilters[key].label = item.label
            } else {
              state.masterFilters[key] = item
            }
          }
        })
        return state.masterFilters
      }
      state.langCode = ''
      return state.masterFilters
    },
    getCurrentPage: state => state.currentPage,
    getPageSize: state => state.pageSize,
    getKendo: state => {
      if (typeof getColumns === 'function') {
        state.kendo.columns = getColumns()
      }
      return state.kendo
    },
    getPaginationInfo: state => {
      state.paginationInfo.pageSize = state.pageSize
      return state.paginationInfo
    },
    getFilters: (state) => {
      state.filters.results = state.pageSize
      state.filters.page = state.currentPage
      if (state.order) {
        state.filters.order = state.order
      } else {
        delete (state.filters.order)
      }
      if (state.sort && state.filters.order) {
        state.filters.sort = state.sort
      } else {
        delete (state.filters.sort)
      }
      return state.filters
    },
    getTags: (state) => {
      const tags = []
      _.each(state.filters, (item, key) => {
        if (state.notUseLikeTags.indexOf(key) === -1) {
          const tag = [key, item.label, item.value]
          const extraparams = discard(['label', 'value'], item)
          if (Object.keys(extraparams).length) {
            tag.extraparams = extraparams
          }
          tags.push(tag)
        }
      })
      return tags
    },
    getAppliedFilters (state) {
      const data = {}
      const _temp = Object.entries(state.masterFilters)
      _.each(_temp, ([key, item]) => {
        if (item.value !== '' && (!Array.isArray(item.value) || item.value.length > 0)) data[key] = item
      })
      state.filters = data
      return data
    },
    isClientFilterActive: state => state.clientFilterActive,
  }, customGetters)
  
  const mutations = _.merge({
    setKendoColumns (state, data) {
      state.kendo.columns = state.kendo.columns.map((x, i) => {
        if (data.includes(x.field)) {
          x.hidden = false
        } else {
          x.hidden = x.className !== 'added-column'
          data.splice(i, 0, x.field)
        }
        return x
      }).sort((a, b) => data.indexOf(a.field) - data.indexOf(b.field))
    },
    setKendo (state, kendo) {
      state.kendo = kendo
    },
    resetState (state) {
      Object.assign(state, getDefaultState())
    },
    resetPaginationInfo (state, data) {
      state.pageSize = DEFAULT_PAGE_SIZE
      state.paginationInfo = {
        total: 0,
        currentPage: 1,
        totalPages: 0,
        pageSize: DEFAULT_PAGE_SIZE
      }
    },
    setPaginationInfo (state, data) {
      state.paginationInfo = data
    },
    setSort (state, fieldName) {
      state.sort = fieldName
    },
    setSortOrder (state, order) {
      switch (order) {
        case 'ascending':
          state.order = 'asc'
          break
        case 'descending':
          state.order = 'desc'
          break
        default:
          state.order = null
          state.sort = null
      }
    },
    setNotUseLikeTags (state, items = []) {
      state.notUseLikeTags = items
    },
    setMasterFilters (state, master) {
      const data = {}
      const _temp = Object.entries(master)
      _.each(_temp, ([key, item]) => {
        if (typeof item === 'object') {
          if (item.value === undefined || item.value === null) {
            item.value = ''
          }
          if (Object.hasOwnProperty.call(state.masterFilters, key)) {
            data[key] = state.masterFilters[key]
            data[key].value = item.value
          } else {
            data[key] = item
          }
        }
      })
      const _tempFilters = Object.entries(state.masterFilters)
      _.each(_tempFilters, ([key, item]) => {
        if (!Object.hasOwnProperty.call(data, key)) {
          data[key] = item
          data[key].value = ''
        }
      })
      state.masterFilters = data
    },
    removeTag (state, tag) {
      const path = tag.split('.')
      if (path.length === 1) {
        state.masterFilters[path[0]] = ''
      }
      if (path.length === 2) {
        state.masterFilters[path[0]][path[1]] = ''
      }
    },
    resetMasterFilters (state) {
      state.masterFilters = _.cloneDeep(masterFilters)
    },
    setFilter (state, { key, value }) {
      Vue.set(state.filters, { key: value })
    },
    setFilters (state, thefilters) {
      Vue.set(state, 'filters', thefilters)
    },
    clearFilters (state) {
      Vue.set(state, 'filters', [])
    },
    clearPagination (state) {
      state.pageSize = DEFAULT_PAGE_SIZE
      state.currentPage = 1
    },
    setPageSize (state, size) {
      state.pageSize = size
    },
    setCurrentPage (state, pageNumner) {
      if (pageNumner > 0 && pageNumner <= state.paginationInfo.totalPages) {
        state.currentPage = pageNumner
        state.paginationInfo.currentPage = pageNumner
      }
    },
    setClientFilterActive (state, active) {
      state.clientFilterActive = active
    }
  }, customMutations)
  
  const actions = _.merge({
    resetCartState ({ commit }) {
      commit('resetState')
    },
  }, customActions)
  return {
    namespaced,
    state,
    getters,
    mutations,
    actions,
    ...rest
  }
}

export default _interface
